import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

/**
 * Current user's profile, served by GET /api/v3/user/me. The SPA uses this instead of decoding an
 * id-token: users authenticate via the LeaseQuery platform ("APIAuth" cookie, httpOnly), not a
 * client-side Keycloak/Duende session.
 */
export interface CurrentUser {
  userId: string;
  userName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUser: CurrentUser | null = null;

  constructor(private injector: Injector) {}

  /**
   * Loads the current user from the API (authenticated by the platform "APIAuth" cookie) and caches
   * it. Called once during app bootstrap. HttpClient is resolved lazily to avoid a DI cycle with the
   * HTTP interceptors (which depend on this service).
   */
  async loadCurrentUser(): Promise<boolean> {
    try {
      const http = this.injector.get(HttpClient);
      this.currentUser = await firstValueFrom(
        http.get<CurrentUser>(`${environment.servicesApiUrl}/api/v3/user/me`),
      );
      return this.currentUser != null;
    } catch {
      this.currentUser = null;
      return false;
    }
  }

  isAuthenticated(): boolean {
    return this.currentUser != null;
  }

  async isAuthenticatedAnywhere(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }
    return this.loadCurrentUser();
  }

  getCurrentUserName(): string {
    if (this.currentUser?.displayName) {
      return this.currentUser.displayName;
    }
    const name = [this.currentUser?.firstName, this.currentUser?.lastName]
      .filter(part => !!part)
      .join(' ')
      .trim();
    return name || this.currentUser?.userName || 'Guest';
  }

  getCurrentUserEmail(): string {
    return this.currentUser?.email ?? '';
  }

  getCurrentLoggedOnUserId(): string {
    return this.currentUser?.userId ?? '';
  }

  getIdentityProvider(): string {
    // No longer sourced from a client-side token — users authenticate via the LeaseQuery platform.
    return '';
  }

  getTenantId(): string {
    // No longer available from a login token; integration flows source the tenant from their own OAuth.
    return '';
  }

  /** Send the user to the FinQuery/CIAM platform login (retained param signature for callers). */
  init(returnUrl: string = window.location.href, _params?: { [key: string]: string }) {
    this.redirectToPlatformLogin(returnUrl);
  }

  redirectToPlatformLogin(returnUrl: string = window.location.href) {
    const base = (environment.lqmsWebApp ?? '').replace(/\/+$/, '');
    if (!base) {
      console.warn(
        'environment.lqmsWebApp is not configured; cannot redirect to the FinQuery platform login.',
      );
      return;
    }

    // The platform login round-trips back via the `redirectUrl` query param (URL-encoded). The
    // path is per environment: `/Login/Identity?status=1` (the standard login page) when deployed,
    // `/?status=12` (the legacy password form) locally — see environment.ts.
    const target = `${base}${environment.lqmsLoginPath}&redirectUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = target;
  }

  logOut(returnUrl: string = window.location.href) {
    // TODO: point at the platform logout endpoint once known (to invalidate the "APIAuth" cookie).
    // For now, clear local state and return the user to the platform login.
    this.currentUser = null;
    this.redirectToPlatformLogin(returnUrl);
  }

  logOutLocal() {
    this.currentUser = null;
  }

  logOutWithParams(_customParams?: unknown) {
    this.logOut();
  }

  resetImplicitFlow() {
    // No-op: the SPA no longer runs a client-side OIDC implicit flow.
  }

  getAccessToken(): string {
    // The SPA holds no client-side token; API calls authenticate via the platform "APIAuth" cookie.
    return '';
  }

  getAccessTokenExpiration(): number | null {
    return null;
  }
}
